// src/components/CompleteProfileModal.js
import React, { useState, useMemo } from 'react';
import { doc, updateDoc } from "firebase/firestore";
import { db } from '../firebase-config';
import { locationData } from '../utils/constants';

const CompleteProfileModal = ({ user, uid, onComplete }) => {
    const [formData, setFormData] = useState({
        phoneNumber: user.phoneNumber || '',
        gender: user.gender || '',
        age: user.age || '',
        dob: user.dob || '',
        state: user.location?.state || '',
        district: user.location?.district || '',
        constituency: user.location?.constituency || '',
        mandal: user.location?.mandal || ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // 1. Memoize Districts based on selected state
    const availableDistricts = useMemo(() => {
        if (!formData.state) return [];
        // Find the state object in your constants file
        const stateObj = locationData.find(s => s.state_en === formData.state);
        return stateObj ? stateObj.districts : [];
    }, [formData.state]);

    // Find the currently selected district object to access constituencies and mandals
    const selectedDistrictObj = useMemo(() => {
        if (!formData.district) return null;
        return availableDistricts.find(d => d.district_en === formData.district) || null;
    }, [formData.district, availableDistricts]);

    // 2. Memoize Constituencies based on selected district
    const availableConstituencies = useMemo(() => {
        return selectedDistrictObj ? selectedDistrictObj.constituencies || [] : [];
    }, [selectedDistrictObj]);

    // 3. Memoize Mandals based on selected district
    const availableMandals = useMemo(() => {
        return selectedDistrictObj ? selectedDistrictObj.mandals || [] : [];
    }, [selectedDistrictObj]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.state || !formData.district || !formData.constituency || !formData.mandal) {
            alert("దయచేసి అన్ని స్థాన వివరాలను ఎంచుకోండి (Please select State, District, Constituency, and Mandal).");
            return;
        }
        setLoading(true);
        setError('');
        try {
            const userRef = doc(db, 'users', uid);
            const updateData = {
                gender: formData.gender,
                age: parseInt(formData.age),
                dob: formData.dob,
                phoneNumber: formData.phoneNumber,
                location: { 
                    state: formData.state, 
                    district: formData.district,
                    constituency: formData.constituency,
                    mandal: formData.mandal
                }
            };

            // This directly updates the Firebase Firestore collection document, syncing with the backend database
            await updateDoc(userRef, updateData);
            
            // Save local storage flag to instantly skip checking on state reload
            localStorage.setItem('profile_completed_session', 'true');
            
            alert("Profile updated successfully!");
            onComplete(); // Close modal in UI

            setTimeout(() => {
                window.location.reload();
            }, 500);

        } catch (err) {
            console.error("Update Error:", err);
            setError("Update failed: " + err.message);
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 20000 }}>
            <div className="modal-content" style={{ maxWidth: '450px', maxHeight: '90vh', overflowY: 'auto' }}>
                <h2 style={{ color: '#d32f2f' }}>⚠️ Complete Your Profile</h2>
                <p>We've updated Ai News! Please provide these missing details to continue using the app and claiming rewards.</p>
                
                {error && <p className="error-message" style={{ color: 'red' }}>{error}</p>}

                <form onSubmit={handleSubmit}>
                    {!user.phoneNumber && (
                        <div className="form-group">
                            <label>Phone Number (10-digit-number)</label>
                            <input 
                                type="tel" 
                                required 
                                pattern="[0-9]{10}"
                                title="Please enter a valid 10-digit phone number"
                                value={formData.phoneNumber} 
                                onChange={e => setFormData({...formData, phoneNumber: e.target.value})} 
                            />
                        </div>
                    )}

                    <div className="form-group">
                        <label>Gender (లింగం)</label>
                        <select required value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})}>
                            <option value="">Select</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="non-binary">Non-binary</option>
                            <option value="prefer-not-to-say">Prefer not to say</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Age (వయస్సు)</label>
                        <input type="number" required min="1" max="120" value={formData.age} onChange={e => setFormData({...formData, age: e.target.value})} />
                    </div>

                    <div className="form-group">
                        <label>Date of Birth (పుట్టిన తేదీ)</label>
                        <input 
                            type="date" 
                            required 
                            max={new Date().toISOString().split('T')[0]} 
                            value={formData.dob} 
                            onChange={e => setFormData({...formData, dob: e.target.value})} 
                        />
                    </div>

                    <div className="form-group">
                        <label>State (రాష్ట్రం)</label>
                        <select 
                            required 
                            value={formData.state} 
                            onChange={e => setFormData({
                                ...formData, 
                                state: e.target.value, 
                                district: '', 
                                constituency: '', 
                                mandal: ''
                            })}
                        >
                            <option value="">Select State</option>
                            {locationData.map(s => s.state_en && <option key={s.state_en} value={s.state_en}>{s.state_te} ({s.state_en})</option>)}
                        </select>
                    </div>

                    {formData.state && (
                        <div className="form-group">
                            <label>District (జిల్లా)</label>
                            <select 
                                required 
                                value={formData.district} 
                                onChange={e => setFormData({
                                    ...formData, 
                                    district: e.target.value, 
                                    constituency: '', 
                                    mandal: ''
                                })}
                            >
                                <option value="">-- Select District --</option>
                                {availableDistricts.map(d => (
                                    <option key={d.district_en} value={d.district_en}>{d.district_te || d.district_en}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {formData.district && (
                        <div className="form-group">
                            <label>Constituency (నియోజకవర్గం)</label>
                            <select 
                                required 
                                value={formData.constituency} 
                                onChange={e => setFormData({
                                    ...formData, 
                                    constituency: e.target.value, 
                                    mandal: ''
                                })}
                            >
                                <option value="">-- Select Constituency --</option>
                                {availableConstituencies.map(c => (
                                    <option key={c.value} value={c.value}>{c.label} ({c.value})</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {formData.constituency && (
                        <div className="form-group">
                            <label>Mandal (మండలం)</label>
                            <select 
                                required 
                                value={formData.mandal} 
                                onChange={e => setFormData({...formData, mandal: e.target.value})}
                            >
                                <option value="">-- Select Mandal --</option>
                                {availableMandals.map(m => (
                                    <option key={m.value} value={m.value}>{m.label} ({m.value})</option>
                                ))}
                            </select>
                        </div>
                    )}
                    
                    <button type="submit" disabled={loading} className="button-primary" style={{ width: '100%', marginTop: '15px' }}>
                        {loading ? 'Saving...' : 'Update & Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CompleteProfileModal;